import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import mammoth from "mammoth";
import * as XLSX from "xlsx";
import { previewKind, type PreviewKind } from "../lib/fileTypes";
import { escapeHtml, sanitizePreviewHtml } from "../lib/sanitizeHtml";
import { fetchDocumentBytes, formatDocumentError } from "../lib/fetchDocument";
import { applyPdfCanvasBacking, pdfCanvasBacking, pdfRenderScale } from "../lib/documentPreviewScale";
import { documentCacheKey, getCachedDocument, setCachedDocument, hasSeenDocument, markSeenDocument } from "../lib/documentCache";
import {
  exitDocumentFullscreen,
  getFullscreenElement,
  isElementFullscreen,
  requestElementFullscreen,
  standalonePreviewScroller,
  subscribeFullscreenChange,
} from "../lib/fullscreen";
import {
  applyPreviewZoom,
  buildZoomAnchor,
  isPinchZoomWheel,
  isPreviewZoomActive,
  isZoomedLayout,
  isLiveZoomPastFit,
  resolvePreviewZoomScroller,
  shouldAcceptZoomStep,
  wheelZoomFactor,
  type ZoomAnchor,
} from "../lib/documentZoom";
import { BrandLoader } from "./BrandLoader";
import { shouldBurnImageWatermark, watermarkImageBlob, watermarkTextFromParts } from "../lib/burnWatermark";
import { useDelayedLoader } from "../hooks/useDelayedLoader";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

// Show a reassuring hint after this long, and hard-fail (rather than hang
// forever) after the ceiling. A data-room viewer must never sit on an endless
// "Loading document…" with no feedback in front of a client.
const LOAD_SLOW_MS = 9000;
const LOAD_HARD_MS = 45000;

// The first preview of a document in a session holds the shield animation for
// two fill cycles so the brand moment lands; repeat opens skip the hold.
const FIRST_VIEW_HOLD_MS = 3000;

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 4;

function clampZoom(scale: number): number {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, scale));
}

function zoomPresetActive(current: number | null, target: number): boolean {
  return Math.abs((current ?? 1) - target) < 0.04;
}

function ZoomLevelIndicator({ active, percent }: { active: boolean; percent: number }) {
  return (
    <span
      className="page-nav__zoom-level"
      aria-live="polite"
      aria-label={active ? `Zoom ${percent} percent` : "Zoom fit to width"}
    >
      {active ? `${percent}%` : "\u00a0"}
    </span>
  );
}

/** Find the element that actually scrolls vertically for a page node. */
function getVerticalScroller(start: HTMLElement): HTMLElement {
  let node: HTMLElement | null = start.parentElement;
  while (node && node !== document.documentElement) {
    const style = getComputedStyle(node);
    const oy = style.overflowY;
    if (
      (oy === "auto" || oy === "scroll" || oy === "overlay") &&
      node.scrollHeight > node.clientHeight + 1
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return (document.scrollingElement as HTMLElement) || document.documentElement;
}

/** Y coordinate just below all sticky chrome overlapping the scroller top. */
function readingProbeY(
  scroller: HTMLElement,
  wrap: HTMLElement | null,
  findOpen: boolean,
): number {
  const scrollerRect = scroller.getBoundingClientRect();
  let probeY = scrollerRect.top + 4;

  const docHeader = scroller.querySelector<HTMLElement>(".viewer-doc-header");
  if (docHeader) {
    const headerRect = docHeader.getBoundingClientRect();
    if (headerRect.bottom > scrollerRect.top) {
      probeY = Math.max(probeY, headerRect.bottom);
    }
  }

  const nav = wrap?.querySelector<HTMLElement>(".page-nav");
  if (nav) {
    const navRect = nav.getBoundingClientRect();
    if (navRect.bottom > scrollerRect.top) {
      probeY = Math.max(probeY, navRect.bottom);
    }
  }

  const find = findOpen ? wrap?.querySelector<HTMLElement>(".pdf-find-bar") : null;
  if (find) {
    const findRect = find.getBoundingClientRect();
    if (findRect.bottom > scrollerRect.top) {
      probeY = Math.max(probeY, findRect.bottom);
    }
  }

  const standaloneBar = wrap
    ?.closest(".preview-standalone-page")
    ?.querySelector<HTMLElement>(".preview-standalone__bar");
  if (standaloneBar) {
    const barRect = standaloneBar.getBoundingClientRect();
    probeY = Math.max(probeY, barRect.bottom);
  }

  return probeY;
}

/** Pick the page under the reading probe using viewport geometry. */
function pickActivePage(
  pages: Map<number, HTMLElement>,
  scroller: HTMLElement,
  wrap: HTMLElement | null,
  findOpen: boolean,
): number {
  const probeY = readingProbeY(scroller, wrap, findOpen);
  let active = 1;
  let bestVisible = 0;
  const sorted = [...pages.entries()].sort((a, b) => a[0] - b[0]);
  for (const [pageNumber, el] of sorted) {
    const rect = el.getBoundingClientRect();
    if (rect.top <= probeY && rect.bottom > probeY) {
      return pageNumber;
    }
    const visibleTop = Math.max(rect.top, probeY);
    const visibleBottom = Math.min(rect.bottom, window.innerHeight);
    const visible = Math.max(0, visibleBottom - visibleTop);
    if (visible > bestVisible) {
      bestVisible = visible;
      active = pageNumber;
    }
  }
  return active;
}

/** Scroll events for document scrolling fire on `window` when the root scroller is used. */
function scrollEventTarget(scroller: HTMLElement): EventTarget {
  const root = document.scrollingElement;
  if (scroller === root || scroller === document.body) return window;
  return scroller;
}

type PreviewSurface = "pdf" | "docx" | "xlsx";

/** Virtual page height for flowed HTML (US Letter aspect). */
function docPageHeightPx(containerWidth: number) {
  const w = Math.max(320, containerWidth);
  return Math.round(w * (11 / 8.5));
}

/** Split flowed HTML nodes into fixed-height virtual pages in one continuous strip. */
function paginateHtmlIntoStrip(
  strip: HTMLElement,
  nodes: Node[],
  pageHeightPx: number,
  extraClass: string,
): Map<number, HTMLElement> {
  const pages = new Map<number, HTMLElement>();
  strip.replaceChildren();
  strip.className = "document-strip pdf-viewer__pages";

  if (nodes.length === 0) {
    const empty = document.createElement("div");
    empty.className = `doc-page ${extraClass}`;
    empty.dataset.page = "1";
    empty.innerHTML = "<p><em>Empty document.</em></p>";
    strip.appendChild(empty);
    pages.set(1, empty);
    return pages;
  }

  const measure = document.createElement("div");
  measure.className = `doc-page ${extraClass}`;
  measure.style.cssText =
    "position:absolute;left:-99999px;top:0;visibility:hidden;pointer-events:none;width:100%;";
  measure.style.width = `${strip.clientWidth || 816}px`;
  document.body.appendChild(measure);

  let pageNum = 1;
  let pageEl = document.createElement("div");
  pageEl.className = `doc-page ${extraClass}`;
  pageEl.dataset.page = String(pageNum);

  const commitPage = () => {
    strip.appendChild(pageEl);
    pages.set(pageNum, pageEl);
    pageNum += 1;
    pageEl = document.createElement("div");
    pageEl.className = `doc-page ${extraClass}`;
    pageEl.dataset.page = String(pageNum);
  };

  for (const node of nodes) {
    pageEl.appendChild(node.cloneNode(true));
    measure.replaceChildren(pageEl.cloneNode(true));
    if (measure.scrollHeight > pageHeightPx && pageEl.childNodes.length > 1) {
      const overflow = pageEl.lastChild!;
      pageEl.removeChild(overflow);
      commitPage();
      pageEl.appendChild(overflow);
    }
  }
  if (pageEl.childNodes.length > 0) commitPage();

  measure.remove();
  return pages;
}

function registerStripMarkers(strip: HTMLElement): Map<number, HTMLElement> {
  const pages = new Map<number, HTMLElement>();
  strip.querySelectorAll<HTMLElement>("[data-page]").forEach((el) => {
    const n = Number(el.dataset.page);
    if (n > 0) pages.set(n, el);
  });
  return pages;
}

type Props = {
  url: string;
  filename: string;
  mimeType?: string;
  sessionId?: string;
  admin?: boolean;
  watermarkEmail?: string;
  viewId?: string;
  documentId: string;
  layout?: "inline" | "standalone";
  onPageEvent?: (ev: { pageNumber: number; dwellMs: number; event: "page_view" | "page_leave" }) => void;
  onCurrentPageChange?: (page: number, pageCount: number) => void;
  onDownload?: () => void;
  initialFindTerm?: string;
};

export function DocumentPreview({
  url,
  filename,
  mimeType,
  sessionId,
  admin,
  watermarkEmail,
  viewId,
  documentId,
  layout = "inline",
  onPageEvent,
  onCurrentPageChange,
  onDownload,
  initialFindTerm,
}: Props) {
  const pagesHostRef = useRef<HTMLDivElement>(null);
  const zoomLayerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const pdfDocRef = useRef<pdfjs.PDFDocumentProxy | null>(null);
  const scrollerRef = useRef<HTMLElement | null>(null);
  const scrollRafRef = useRef<number | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [slowLoad, setSlowLoad] = useState(false);
  const pageRefs = useRef<Map<number, HTMLElement>>(new Map());
  const onPageEventRef = useRef(onPageEvent);
  onPageEventRef.current = onPageEvent;
  const onCurrentPageChangeRef = useRef(onCurrentPageChange);
  onCurrentPageChangeRef.current = onCurrentPageChange;
  const pageCountRef = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const [previewSurface, setPreviewSurface] = useState<PreviewSurface | null>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [sheetHtml, setSheetHtml] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [markersReady, setMarkersReady] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [zoomScale, setZoomScale] = useState<number | null>(null);
  const zoomScaleRef = useRef<number | null>(null);
  const liveZoomRef = useRef(1);
  const pinchActiveRef = useRef(false);
  const pinchIdleTimerRef = useRef<number | null>(null);
  const gestureActiveRef = useRef(false);
  const lastPointerRef = useRef({ x: 0, y: 0 });
  const zoomStateRafRef = useRef<number | null>(null);
  const [pageInput, setPageInput] = useState("1");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const wasFullscreenRef = useRef(false);
  const [servingPdf, setServingPdf] = useState(false);
  const [processing, setProcessing] = useState(false);
  const seenKey = documentId || url;
  const firstView = !hasSeenDocument(seenKey);
  // First open: show the loader immediately (no 220ms grace) so the hold is
  // a deliberate brand moment, not a blank flash.
  const showDocLoader = useDelayedLoader(loading, firstView ? 0 : 220);
  const showProcessingLoader = useDelayedLoader(processing && loading);
  const [findOpen, setFindOpen] = useState(Boolean(initialFindTerm?.trim()));
  const [findQuery, setFindQuery] = useState(initialFindTerm ?? "");
  const [findMatches, setFindMatches] = useState<Array<{ page: number }>>([]);
  const [findPos, setFindPos] = useState(0);
  const dwellRef = useRef<Map<number, { entered: number }>>(new Map());
  const findOpenRef = useRef(findOpen);
  findOpenRef.current = findOpen;
  const kind = previewKind(filename, mimeType);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || typeof ResizeObserver === "undefined") return;
    if (previewSurface !== "pdf" && previewSurface !== "docx") return;
    let last = wrap.clientWidth;
    let t: number | undefined;
    const ro = new ResizeObserver(() => {
      // Pinch zoom changes layout width (scrollbar). Never reload PDF while zoomed.
      if (isPreviewZoomActive(zoomScaleRef.current, liveZoomRef.current, pinchActiveRef.current)) return;
      const w = wrap.clientWidth;
      if (Math.abs(w - last) < 32) return;
      last = w;
      window.clearTimeout(t);
      t = window.setTimeout(() => setReloadKey((k) => k + 1), 220);
    });
    ro.observe(wrap);
    return () => {
      ro.disconnect();
      window.clearTimeout(t);
    };
  }, [previewSurface]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    const controller = new AbortController();
    let loadingTask: pdfjs.PDFDocumentLoadingTask | null = null;
    let timedOut = false;

    const slowTimer = window.setTimeout(() => {
      if (!cancelled) setSlowLoad(true);
    }, LOAD_SLOW_MS);
    const hardTimer = window.setTimeout(() => {
      if (cancelled) return;
      // The fetch or the pdf.js parse has stalled. Abort both so the promise
      // rejects, then surface a recoverable error instead of hanging.
      timedOut = true;
      controller.abort();
      loadingTask?.destroy().catch(() => undefined);
      setProcessing(false);
      setError(formatDocumentError("load_timeout"));
      setLoading(false);
    }, LOAD_HARD_MS);
    const clearTimers = () => {
      window.clearTimeout(slowTimer);
      window.clearTimeout(hardTimer);
    };

    // Hold the reveal on a document's first open so the shield animation can
    // play through; later opens (and errors) reveal immediately.
    const startedAt = Date.now();
    const holdMs = hasSeenDocument(seenKey) ? 0 : FIRST_VIEW_HOLD_MS;
    const finishLoading = () => {
      const remaining = startedAt + holdMs - Date.now();
      const done = () => {
        if (cancelled) return;
        markSeenDocument(seenKey);
        setLoading(false);
      };
      if (remaining > 0) {
        window.setTimeout(done, remaining);
      } else {
        done();
      }
    };

    (async () => {
      setError(null);
      setPreviewSurface(null);
      setHtml(null);
      setSheetHtml(null);
      setImageUrl(null);
      setPageCount(0);
      pageCountRef.current = 0;
      setMarkersReady(0);
      setServingPdf(false);
      setProcessing(false);
      setSlowLoad(false);
      setLoading(true);
      setZoomScale(null);
      try {
        const cacheKey = documentCacheKey(
          url,
          sessionId,
          admin ? localStorage.getItem("adminSessionToken") : null,
        );
        const cached = getCachedDocument(cacheKey);
        const fetched =
          cached ??
          (await fetchDocumentBytes(url, {
            sessionId,
            admin,
            signal: controller.signal,
          }));
        if (!cached) {
          setCachedDocument(cacheKey, fetched);
        }
        const { buf: rawBuf, contentType, filename: headerName, previewKind: resolved, servingPdf: derivative } = fetched;
        // pdf.js can detach ArrayBuffers; always work on a copy so the cache stays valid.
        const buf = rawBuf.slice(0);
        if (!cancelled) setServingPdf(Boolean(derivative));
        const name = headerName || filename;
        const kindResolved: PreviewKind = resolved || previewKind(name, contentType || mimeType);

        if (kindResolved === "unsupported") {
          throw new Error("unsupported_type");
        }

        if (kindResolved === "docx") {
          const result = await mammoth.convertToHtml({ arrayBuffer: buf });
          if (!cancelled) {
            setPreviewSurface("docx");
            setHtml(sanitizePreviewHtml(result.value || "<p><em>Empty document.</em></p>"));
            finishLoading();
          }
          return;
        }

        if (kindResolved === "xlsx") {
          const wb = XLSX.read(buf, { type: "array" });
          const parts = wb.SheetNames.map((sheetName, i) => {
            const sheet = wb.Sheets[sheetName];
            const table = XLSX.utils.sheet_to_html(sheet, { id: `sheet-${i}` });
            return `<section class="doc-page sheet-block" data-page="${i + 1}"><h3>${escapeHtml(sheetName)}</h3>${table}</section>`;
          });
          if (!cancelled) {
            setPreviewSurface("xlsx");
            setSheetHtml(sanitizePreviewHtml(parts.join("") || "<p><em>Empty workbook.</em></p>"));
            finishLoading();
          }
          return;
        }

        if (kindResolved === "image") {
          const wmInput = {
            email: watermarkEmail,
            viewId,
            timestamp: new Date().toISOString(),
          };
          const mime = contentType || "image/*";
          const blob = new Blob([buf], { type: mime });
          const stamped = shouldBurnImageWatermark(wmInput)
            ? await watermarkImageBlob(blob, watermarkTextFromParts(wmInput))
            : blob;
          objectUrl = URL.createObjectURL(stamped);
          if (!cancelled) {
            setImageUrl(objectUrl);
            finishLoading();
            onPageEventRef.current?.({ pageNumber: 1, dwellMs: 0, event: "page_view" });
          }
          return;
        }

        // PDF: load from bytes (more reliable than blob URLs with pdf.js worker)
        loadingTask = pdfjs.getDocument({ data: new Uint8Array(buf) });
        const doc = await loadingTask.promise;
        if (cancelled) return;
        pdfDocRef.current = doc;
        setPreviewSurface("pdf");
        setPageCount(doc.numPages);
        pageCountRef.current = doc.numPages;
        setCurrentPage(1);
        onCurrentPageChangeRef.current?.(1, doc.numPages);
        pageRefs.current.clear();
        const root = pagesHostRef.current;
        if (!root) return;
        root.replaceChildren();
        root.className = "document-strip pdf-viewer__pages";
        const containerWidth = root.parentElement?.clientWidth || window.innerWidth;

        for (let i = 1; i <= doc.numPages; i++) {
          if (cancelled) return;
          const page = await doc.getPage(i);
          const base = page.getViewport({ scale: 1 });
          const { cssScale, outputScale } = pdfRenderScale(
            base.width,
            containerWidth,
            layout,
            window.devicePixelRatio || 1,
          );
          const viewport = page.getViewport({ scale: cssScale });
          const backing = pdfCanvasBacking(viewport.width, viewport.height, outputScale);
          const wrap = document.createElement("div");
          wrap.className = "pdf-page";
          wrap.dataset.page = String(i);
          wrap.setAttribute("role", "img");
          wrap.setAttribute("aria-label", `Page ${i} of ${doc.numPages}`);
          wrap.style.width = `${backing.cssWidth}px`;

          const canvas = document.createElement("canvas");
          applyPdfCanvasBacking(canvas, backing);
          const ctx = canvas.getContext("2d", { alpha: false });
          if (!ctx) throw new Error("preview_canvas_unavailable");
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = "high";
          wrap.appendChild(canvas);

          root.appendChild(wrap);
          pageRefs.current.set(i, wrap);
          await page.render({
            canvasContext: ctx,
            viewport,
            transform: backing.transform,
            intent: "print",
          }).promise;
        }

        if (cancelled) return;

        if (!cancelled) {
          setMarkersReady((k) => k + 1);
          setProcessing(false);
          finishLoading();
        }
      } catch (e) {
        // The timeout handler and unmount already set final state; don't clobber
        // it with a generic abort error.
        if (timedOut || cancelled) return;
        const raw = String((e as Error).message || e);
        if (raw === "not_ready") {
          setProcessing(true);
          setError(null);
          setLoading(true);
        } else {
          setProcessing(false);
          setError(
            raw === "unsupported_type"
              ? "Preview not available for this file type. Use Download to open locally."
              : formatDocumentError(raw),
          );
          setLoading(false);
        }
      } finally {
        clearTimers();
      }
    })();

    return () => {
      cancelled = true;
      clearTimers();
      controller.abort();
      loadingTask?.destroy().catch(() => undefined);
      pageRefs.current.clear();
      pdfDocRef.current = null;
      pagesHostRef.current?.replaceChildren();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      void documentId;
    };
  }, [url, sessionId, admin, watermarkEmail, viewId, documentId, reloadKey, filename, mimeType, kind, layout, seenKey]);

  useEffect(() => {
    return () => {
      if (getFullscreenElement()) {
        exitDocumentFullscreen().catch(() => undefined);
      }
    };
  }, []);

  // DOCX/XLSX: paginate or register page markers in the shared continuous strip.
  useLayoutEffect(() => {
    if (loading || !previewSurface || previewSurface === "pdf") return;
    const strip = pagesHostRef.current;
    if (!strip) return;

    pageRefs.current.clear();
    dwellRef.current.clear();

    if (previewSurface === "docx" && html) {
      const temp = document.createElement("div");
      temp.className = "docx-preview";
      temp.innerHTML = html;
      const nodes = [...temp.childNodes];
      const width = strip.parentElement?.clientWidth || strip.clientWidth || window.innerWidth;
      pageRefs.current = paginateHtmlIntoStrip(
        strip,
        nodes,
        docPageHeightPx(width),
        "docx-preview",
      );
    } else if (previewSurface === "xlsx" && sheetHtml) {
      strip.className = "document-strip pdf-viewer__pages sheet-preview";
      strip.innerHTML = sheetHtml;
      pageRefs.current = registerStripMarkers(strip);
    }

    const wmText = watermarkTextFromParts({
      email: watermarkEmail,
      viewId,
      timestamp: new Date().toISOString(),
    });
    if (wmText) {
      strip.querySelectorAll<HTMLElement>(".doc-page").forEach((page) => {
        const overlay = document.createElement("div");
        overlay.className = "wm-overlay";
        overlay.setAttribute("aria-hidden", "true");
        overlay.textContent = wmText;
        page.appendChild(overlay);
      });
    }

    const total = pageRefs.current.size;
    setPageCount(total);
    pageCountRef.current = total;
    setCurrentPage(1);
    onCurrentPageChangeRef.current?.(1, total);
    setMarkersReady((k) => k + 1);
  }, [loading, previewSurface, html, sheetHtml, reloadKey, watermarkEmail, viewId]);

  // Dwell analytics: one observer for all page markers (PDF, DOCX, XLSX).
  useEffect(() => {
    if (loading || pageRefs.current.size === 0) return;

    const firstPage = pageRefs.current.get(1);
    if (!firstPage) return;

    const scroller = getVerticalScroller(firstPage);
    const ioRoot =
      scroller &&
      scroller !== document.documentElement &&
      scroller !== document.body
        ? scroller
        : null;

    const pageObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const pageNumber = Number((entry.target as HTMLElement).dataset.page);
          if (!pageNumber) continue;
          if (entry.isIntersecting) {
            if (!dwellRef.current.has(pageNumber)) {
              dwellRef.current.set(pageNumber, { entered: performance.now() });
              onPageEventRef.current?.({ pageNumber, dwellMs: 0, event: "page_view" });
            }
          } else if (dwellRef.current.has(pageNumber)) {
            const started = dwellRef.current.get(pageNumber)!.entered;
            const dwellMs = Math.round(performance.now() - started);
            dwellRef.current.delete(pageNumber);
            onPageEventRef.current?.({ pageNumber, dwellMs, event: "page_leave" });
          }
        }
      },
      {
        root: ioRoot,
        rootMargin: "0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    );
    for (const [, el] of pageRefs.current) pageObserver.observe(el);
    return () => pageObserver.disconnect();
  }, [loading, markersReady, pageCount, isFullscreen, reloadKey]);

  // Scroll spy: rebind when fullscreen, find bar, or layout changes the scroller.
  useEffect(() => {
    if (loading || pageCount < 1 || pageRefs.current.size === 0) return;

    const firstPage = pageRefs.current.get(1);
    if (!firstPage) return;

    const scroller = getVerticalScroller(firstPage);
    scrollerRef.current = scroller;
    const scrollTarget = scrollEventTarget(scroller);

    const syncActivePage = () => {
      if (!scrollerRef.current || pageRefs.current.size === 0) return;
      const active = pickActivePage(
        pageRefs.current,
        scrollerRef.current,
        wrapRef.current,
        findOpenRef.current,
      );
      setCurrentPage((prev) => {
        if (prev === active) return prev;
        onCurrentPageChangeRef.current?.(active, pageCountRef.current);
        return active;
      });
    };

    const onScrollerScroll = () => {
      if (scrollRafRef.current != null) return;
      scrollRafRef.current = window.requestAnimationFrame(() => {
        scrollRafRef.current = null;
        syncActivePage();
      });
    };

    const onScrollerScrollEnd = () => {
      syncActivePage();
    };

    scrollTarget.addEventListener("scroll", onScrollerScroll, { passive: true });
    scrollTarget.addEventListener("scrollend", onScrollerScrollEnd);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(syncActivePage);
    });

    return () => {
      scrollTarget.removeEventListener("scroll", onScrollerScroll);
      scrollTarget.removeEventListener("scrollend", onScrollerScrollEnd);
      if (scrollRafRef.current != null) {
        window.cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, [loading, pageCount, isFullscreen, findOpen, reloadKey, markersReady, layout]);

  useEffect(() => {
    if (!processing) return;
    const t = window.setInterval(() => setReloadKey((k) => k + 1), 2200);
    return () => window.clearInterval(t);
  }, [processing]);

  const scrollToPage = useCallback((n: number) => {
    const el = pageRefs.current.get(n);
    if (!el) return;
    const scroller = getVerticalScroller(el);
    scrollerRef.current = scroller;
    const probeY = readingProbeY(scroller, wrapRef.current, findOpenRef.current);
    const delta = el.getBoundingClientRect().top - probeY;
    scroller.scrollBy({ top: delta, behavior: "smooth" });
    setCurrentPage(n);
    setPageInput(String(n));
    onCurrentPageChangeRef.current?.(n, pageCountRef.current);
  }, []);

  useEffect(() => {
    const q = findQuery.trim().toLowerCase();
    if (!q || !pdfDocRef.current || loading) {
      setFindMatches([]);
      setFindPos(0);
      return;
    }
    let cancelled = false;
    (async () => {
      const hits: Array<{ page: number }> = [];
      const pdf = pdfDocRef.current!;
      for (let p = 1; p <= pdf.numPages; p++) {
        const page = await pdf.getPage(p);
        const tc = await page.getTextContent();
        const text = tc.items.map((i) => ("str" in i ? String(i.str) : "")).join(" ").toLowerCase();
        if (text.includes(q)) hits.push({ page: p });
      }
      if (cancelled) return;
      setFindMatches(hits);
      setFindPos(0);
      if (hits[0]) scrollToPage(hits[0].page);
    })().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [findQuery, loading, reloadKey, scrollToPage]);

  useEffect(() => {
    if (initialFindTerm?.trim()) {
      setFindQuery(initialFindTerm);
      setFindOpen(true);
    }
  }, [initialFindTerm]);

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const layer = zoomLayerRef.current;
    if (!wrap || !layer) return;
    if (pinchActiveRef.current) return;

    if (zoomScale === null) {
      // Live pinch may run before React commits zoomScale; do not snap back to Fit.
      if (pinchActiveRef.current || isLiveZoomPastFit(liveZoomRef.current)) return;
      liveZoomRef.current = 1;
      zoomScaleRef.current = null;
      applyPreviewZoom(wrap, layer, 1, { fitMode: true });
      return;
    }

    if (zoomScaleRef.current === zoomScale && layer.style.zoom === String(zoomScale)) return;

    liveZoomRef.current = zoomScale;
    zoomScaleRef.current = zoomScale;
    applyPreviewZoom(wrap, layer, zoomScale);
  }, [zoomScale, loading, previewSurface]);

  const scheduleZoomState = useCallback((next: number) => {
    if (zoomStateRafRef.current != null) cancelAnimationFrame(zoomStateRafRef.current);
    zoomStateRafRef.current = requestAnimationFrame(() => {
      zoomStateRafRef.current = null;
      setZoomScale(next);
    });
  }, []);

  const applyLiveZoom = useCallback((newZoom: number, anchor: ZoomAnchor | null) => {
    const wrap = wrapRef.current;
    const layer = zoomLayerRef.current;
    if (!wrap || !layer) return;
    pinchActiveRef.current = true;
    liveZoomRef.current = newZoom;
    zoomScaleRef.current = newZoom;
    applyPreviewZoom(wrap, layer, newZoom, { anchor });
    scheduleZoomState(newZoom);
    if (pinchIdleTimerRef.current != null) window.clearTimeout(pinchIdleTimerRef.current);
    pinchIdleTimerRef.current = window.setTimeout(() => {
      pinchActiveRef.current = false;
      pinchIdleTimerRef.current = null;
    }, 280);
  }, [scheduleZoomState]);

  useEffect(() => {
    return () => {
      if (zoomStateRafRef.current != null) cancelAnimationFrame(zoomStateRafRef.current);
      if (pinchIdleTimerRef.current != null) window.clearTimeout(pinchIdleTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || loading) return;

    let gestureStart = 1;
    const scroller = resolvePreviewZoomScroller(wrap);
    const targets = new Set<EventTarget>([wrap, scroller]);
    if (viewerRef.current) targets.add(viewerRef.current);
    if (zoomLayerRef.current) targets.add(zoomLayerRef.current);

    const eventInPreview = (e: Event) => {
      const target = e.target;
      if (target instanceof Node && wrap.contains(target)) return true;
      if (e instanceof WheelEvent || (e as Event & { clientX?: number }).clientX != null) {
        const rect = wrap.getBoundingClientRect();
        const x = (e as WheelEvent).clientX;
        const y = (e as WheelEvent).clientY;
        return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
      }
      return false;
    };

    const onWheel = (e: Event) => {
      if (!eventInPreview(e)) return;
      const we = e as WheelEvent;
      if (gestureActiveRef.current) return;
      if (!isPinchZoomWheel(we)) return;
      we.preventDefault();
      we.stopPropagation();
      lastPointerRef.current = { x: we.clientX, y: we.clientY };
      const layer = zoomLayerRef.current;
      if (!layer) return;
      const oldZoom = liveZoomRef.current;
      const newZoom = clampZoom(oldZoom * wheelZoomFactor(we.deltaY, we.deltaMode));
      if (!shouldAcceptZoomStep(oldZoom, newZoom)) return;
      applyLiveZoom(
        newZoom,
        buildZoomAnchor(wrap, layer, we.clientX, we.clientY, oldZoom, newZoom),
      );
    };

    const onPointerMove = (e: Event) => {
      const pe = e as PointerEvent;
      lastPointerRef.current = { x: pe.clientX, y: pe.clientY };
    };

    const onGestureStart = (e: Event) => {
      if (!eventInPreview(e)) return;
      e.preventDefault();
      gestureActiveRef.current = true;
      gestureStart = liveZoomRef.current;
    };

    const onGestureChange = (e: Event) => {
      if (!eventInPreview(e)) return;
      e.preventDefault();
      const ge = e as Event & { scale?: number; clientX?: number; clientY?: number };
      const scale = ge.scale ?? 1;
      const layer = zoomLayerRef.current;
      if (!layer) return;
      const oldZoom = liveZoomRef.current;
      const newZoom = clampZoom(gestureStart * scale);
      if (!shouldAcceptZoomStep(oldZoom, newZoom)) return;
      const px = ge.clientX && ge.clientX > 0 ? ge.clientX : lastPointerRef.current.x;
      const py = ge.clientY && ge.clientY > 0 ? ge.clientY : lastPointerRef.current.y;
      applyLiveZoom(
        newZoom,
        buildZoomAnchor(wrap, layer, px, py, oldZoom, newZoom),
      );
    };

    const onGestureEnd = (e: Event) => {
      if (!eventInPreview(e)) return;
      e.preventDefault();
      gestureActiveRef.current = false;
      gestureStart = liveZoomRef.current;
    };

    const wheelOpts: AddEventListenerOptions = { passive: false, capture: true };
    const gestureOpts: AddEventListenerOptions = { passive: false, capture: true };
    const pointerOpts: AddEventListenerOptions = { passive: true, capture: true };
    wrap.addEventListener("pointermove", onPointerMove, pointerOpts);
    for (const target of targets) {
      target.addEventListener("wheel", onWheel, wheelOpts);
      target.addEventListener("gesturestart", onGestureStart, gestureOpts);
      target.addEventListener("gesturechange", onGestureChange, gestureOpts);
      target.addEventListener("gestureend", onGestureEnd, gestureOpts);
      target.addEventListener("gesturecancel", onGestureEnd, gestureOpts);
    }
    document.addEventListener("wheel", onWheel, wheelOpts);
    document.addEventListener("gesturestart", onGestureStart, gestureOpts);
    document.addEventListener("gesturechange", onGestureChange, gestureOpts);
    document.addEventListener("gestureend", onGestureEnd, gestureOpts);
    document.addEventListener("gesturecancel", onGestureEnd, gestureOpts);
    return () => {
      wrap.removeEventListener("pointermove", onPointerMove, pointerOpts);
      for (const target of targets) {
        target.removeEventListener("wheel", onWheel, wheelOpts);
        target.removeEventListener("gesturestart", onGestureStart, gestureOpts);
        target.removeEventListener("gesturechange", onGestureChange, gestureOpts);
        target.removeEventListener("gestureend", onGestureEnd, gestureOpts);
        target.removeEventListener("gesturecancel", onGestureEnd, gestureOpts);
      }
      document.removeEventListener("wheel", onWheel, wheelOpts);
      document.removeEventListener("gesturestart", onGestureStart, gestureOpts);
      document.removeEventListener("gesturechange", onGestureChange, gestureOpts);
      document.removeEventListener("gestureend", onGestureEnd, gestureOpts);
      document.removeEventListener("gesturecancel", onGestureEnd, gestureOpts);
    };
  }, [loading, layout, applyLiveZoom]);

  useEffect(() => {
    const syncFullscreen = () => {
      const el = wrapRef.current;
      const active = isElementFullscreen(el);
      if (wasFullscreenRef.current && !active && el) {
        const savedScroll = resolvePreviewZoomScroller(el).scrollTop;
        const main = standalonePreviewScroller(el);
        requestAnimationFrame(() => {
          if (main) main.scrollTop = savedScroll;
        });
      }
      wasFullscreenRef.current = active;
      setIsFullscreen(active);
    };
    syncFullscreen();
    return subscribeFullscreenChange(syncFullscreen);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    const el = wrapRef.current;
    if (!el) return;

    if (isElementFullscreen(el)) {
      const savedScroll = resolvePreviewZoomScroller(el).scrollTop;
      const main = standalonePreviewScroller(el);
      try {
        await exitDocumentFullscreen();
        requestAnimationFrame(() => {
          if (main) main.scrollTop = savedScroll;
        });
      } catch (err) {
        console.warn("[preview] exit fullscreen failed", err);
      }
      return;
    }

    const scroller = standalonePreviewScroller(el) ?? resolvePreviewZoomScroller(el);
    const savedScroll = scroller.scrollTop;
    try {
      await requestElementFullscreen(el);
      requestAnimationFrame(() => {
        const fsScroller = standalonePreviewScroller(el) ?? resolvePreviewZoomScroller(el);
        fsScroller.scrollTop = savedScroll;
      });
    } catch (err) {
      console.warn("[preview] enter fullscreen failed", err);
    }
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.key === "f" || e.key === "F") {
        e.preventDefault();
        void toggleFullscreen();
        return;
      }
      if ((e.key === "/" || (e.metaKey && e.key === "f")) && previewSurface === "pdf" && pageCount >= 1) {
        e.preventDefault();
        setFindOpen(true);
        return;
      }
      if (pageCount <= 1) return;
      if (e.key === "ArrowLeft" && currentPage > 1) {
        e.preventDefault();
        scrollToPage(currentPage - 1);
      }
      if (e.key === "ArrowRight" && currentPage < pageCount) {
        e.preventDefault();
        scrollToPage(currentPage + 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [currentPage, pageCount, scrollToPage, toggleFullscreen, previewSurface]);

  const pageNavLabel = previewSurface === "xlsx" && pageCount > 1 ? "Sheet" : "Page";

  if (error) {
    return (
      <div className="preview-error card stack" role="alert">
        <p style={{ margin: 0 }}>{error}</p>
        <div className="row toolbar">
          <button type="button" className="secondary" onClick={() => setReloadKey((k) => k + 1)}>
            Retry
          </button>
          {onDownload ? (
            <button type="button" onClick={onDownload}>
              Download instead
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  if (processing && loading) {
    if (!showProcessingLoader) {
      return <div className="preview-processing card" style={{ minHeight: 120 }} aria-hidden />;
    }
    return (
      <div className="preview-processing card stack brand-loader-inline" role="status">
        <BrandLoader label="Converting document for preview…" />
        <p className="muted" style={{ margin: 0, fontSize: 13, textAlign: "center" }}>
          Office files are converted to PDF. This usually takes under a minute.
        </p>
      </div>
    );
  }

  if (imageUrl) {
    const effectiveZoom = zoomScale ?? liveZoomRef.current;
    const zoomPercent = Math.round(effectiveZoom * 100);
    const fitMode = zoomScale === null;
    const zoomActive = isZoomedLayout(effectiveZoom, fitMode);
    return (
      <div
        ref={wrapRef}
        className={[
          "doc-preview",
          "image-preview",
          "document-preview-wrap",
          layout === "standalone" ? " preview-standalone" : "",
          isFullscreen ? " is-fullscreen" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="page-nav" role="toolbar" aria-label="Zoom">
          <div className="page-nav__zoom row toolbar">
            <button
              type="button"
              className={zoomScale === null ? "" : "secondary"}
              onClick={() => {
                pinchActiveRef.current = false;
                liveZoomRef.current = 1;
                zoomScaleRef.current = null;
                setZoomScale(null);
              }}
            >
              Fit
            </button>
            <button
              type="button"
              className={zoomPresetActive(zoomScale, 1.25) ? "" : "secondary"}
              onClick={() => setZoomScale(1.25)}
            >
              125%
            </button>
            <button
              type="button"
              className={zoomPresetActive(zoomScale, 1.5) ? "" : "secondary"}
              onClick={() => setZoomScale(1.5)}
            >
              150%
            </button>
            <ZoomLevelIndicator active={zoomActive} percent={zoomPercent} />
          </div>
          <button
            type="button"
            className="secondary page-nav__fullscreen"
            onClick={() => void toggleFullscreen()}
            aria-pressed={isFullscreen}
            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {isFullscreen ? "Exit" : "Fullscreen"}
          </button>
        </div>
        <div className="image-preview__viewport">
          <div ref={zoomLayerRef} className="document-zoom-layer image-preview__stage">
            <img src={imageUrl} alt={filename} />
          </div>
        </div>
      </div>
    );
  }

  const showPagedChrome = !loading && previewSurface && pageCount >= 1;
  const effectiveZoom = zoomScale ?? liveZoomRef.current;
  const zoomPercent = Math.round(effectiveZoom * 100);
  const fitMode = zoomScale === null;
  const zoomActive = isZoomedLayout(effectiveZoom, fitMode);
  const wmBannerText =
    previewSurface === "docx" || previewSurface === "xlsx"
      ? watermarkTextFromParts({
          email: watermarkEmail,
          viewId,
          timestamp: new Date().toISOString(),
        })
      : "";

  return (
    <div
      ref={wrapRef}
      className={[
        "pdf-preview-wrap",
        "document-preview-wrap",
        layout === "standalone" ? "preview-standalone" : "",
        isFullscreen ? " is-fullscreen" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {showPagedChrome ? (
        <div
          className="page-nav"
          role="toolbar"
          aria-label="Page navigation"
          aria-keyshortcuts="ArrowLeft ArrowRight f /"
        >
          {layout !== "standalone" && servingPdf ? (
            <span className="page-nav__badge">PDF preview of {filename}</span>
          ) : layout !== "standalone" && previewSurface === "docx" ? (
            <span className="page-nav__badge">Document preview of {filename}</span>
          ) : layout !== "standalone" && previewSurface === "xlsx" ? (
            <span className="page-nav__badge">Workbook preview of {filename}</span>
          ) : null}
          {previewSurface === "pdf" ? (
            <button type="button" className="secondary page-nav__find" onClick={() => setFindOpen((v) => !v)}>
              Find
            </button>
          ) : null}
          {previewSurface ? (
            <div className="page-nav__zoom row toolbar">
              <button
                type="button"
                className={zoomScale === null ? "" : "secondary"}
                onClick={() => {
                  pinchActiveRef.current = false;
                  liveZoomRef.current = 1;
                  zoomScaleRef.current = null;
                  setZoomScale(null);
                }}
              >
                Fit
              </button>
              <button
                type="button"
                className={zoomPresetActive(zoomScale, 1.25) ? "" : "secondary"}
                onClick={() => setZoomScale(1.25)}
              >
                125%
              </button>
              <button
                type="button"
                className={zoomPresetActive(zoomScale, 1.5) ? "" : "secondary"}
                onClick={() => setZoomScale(1.5)}
              >
                150%
              </button>
              <ZoomLevelIndicator active={zoomActive} percent={zoomPercent} />
            </div>
          ) : null}
          {pageCount > 1 ? (
            <>
              <button type="button" className="secondary" disabled={currentPage <= 1} onClick={() => scrollToPage(currentPage - 1)}>
                Previous
              </button>
              <span className="page-nav__label">
                {pageNavLabel}{" "}
                <input
                  className="input page-nav__input"
                  type="number"
                  min={1}
                  max={pageCount}
                  value={pageInput}
                  aria-label={`${pageNavLabel} number`}
                  onChange={(e) => setPageInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const n = Math.min(pageCount, Math.max(1, Number(e.currentTarget.value) || 1));
                      scrollToPage(n);
                    }
                  }}
                  onBlur={() => {
                    const n = Math.min(pageCount, Math.max(1, Number(pageInput) || 1));
                    scrollToPage(n);
                  }}
                />{" "}
                of {pageCount}
              </span>
              <button
                type="button"
                className="secondary"
                disabled={currentPage >= pageCount}
                onClick={() => scrollToPage(currentPage + 1)}
              >
                Next
              </button>
            </>
          ) : (
            <span className="page-nav__label">1 {pageNavLabel.toLowerCase()}</span>
          )}
          <button
            type="button"
            className="secondary page-nav__fullscreen"
            onClick={() => void toggleFullscreen()}
            aria-pressed={isFullscreen}
            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {isFullscreen ? "Exit" : "Fullscreen"}
          </button>
          {onDownload && layout !== "standalone" ? (
            <button type="button" className="secondary" onClick={onDownload}>
              Download
            </button>
          ) : null}
        </div>
      ) : null}
      {findOpen && previewSurface === "pdf" ? (
        <div className="pdf-find-bar row toolbar">
          <input
            className="input pdf-find-bar__input"
            type="search"
            placeholder="Find in document"
            value={findQuery}
            aria-label="Find in document"
            onChange={(e) => setFindQuery(e.target.value)}
          />
          <span className="muted pdf-find-bar__count">
            {findMatches.length ? `${findPos + 1} / ${findMatches.length}` : findQuery.trim() ? "No matches" : ""}
          </span>
          <button
            type="button"
            className="secondary"
            disabled={!findMatches.length}
            onClick={() => {
              const next = (findPos - 1 + findMatches.length) % findMatches.length;
              setFindPos(next);
              scrollToPage(findMatches[next].page);
            }}
          >
            Prev
          </button>
          <button
            type="button"
            className="secondary"
            disabled={!findMatches.length}
            onClick={() => {
              const next = (findPos + 1) % findMatches.length;
              setFindPos(next);
              scrollToPage(findMatches[next].page);
            }}
          >
            Next
          </button>
          <button type="button" className="ghost" onClick={() => { setFindOpen(false); setFindQuery(""); }}>
            Close
          </button>
        </div>
      ) : null}
      <div ref={viewerRef} className="pdf-viewer document-viewer">
        {showDocLoader ? (
          <div className="pdf-viewer__loading" role="status">
            <BrandLoader className="brand-loader--preview" label="Loading document…" />
            {slowLoad ? (
              <>
                <p className="muted" style={{ margin: "6px 0 0", fontSize: 13 }}>
                  Still working. Larger files can take a moment.
                </p>
                {onDownload ? (
                  <button type="button" className="secondary" style={{ marginTop: 10 }} onClick={onDownload}>
                    Download instead
                  </button>
                ) : null}
              </>
            ) : null}
          </div>
        ) : null}
        <div ref={zoomLayerRef} className="document-zoom-layer">
          {wmBannerText ? (
            <div className="wm-banner" aria-hidden="true">
              {wmBannerText}
            </div>
          ) : null}
          <div
            ref={pagesHostRef}
            className={`document-strip pdf-viewer__pages${previewSurface === "docx" ? " docx-preview" : ""}${previewSurface === "xlsx" ? " sheet-preview" : ""}`}
            aria-busy={loading}
          />
        </div>
      </div>
    </div>
  );
}
